/**
 * TermTooltip — 术语 hover/click tooltip
 * Phase 101 P1-2
 *
 * - Portal 渲染到 document.body，避免 overflow:hidden 裁切
 * - hover 显示（桌面端） + onClick toggle（移动端）
 * - 无匹配术语时原样渲染 children
 * - 支持引擎前缀：engine="liuyao" → lookup "liuyao:用神" 优先
 */

import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { GLOSSARY } from "../constants/glossary";

export default function TermTooltip({ term, engine = null, children }) {
  const [show, setShow] = useState(false);
  const triggerRef = useRef(null);

  // 引擎前缀优先，否则通用
  const key = engine ? `${engine}:${term}` : term;
  const explanation = GLOSSARY[key] || GLOSSARY[term];

  if (!explanation) return <>{children || term}</>;

  return (
    <>
      <span
        ref={triggerRef}
        style={{
          borderBottom: "1px dotted #D4AF37",
          cursor: "help",
          display: "inline",
        }}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={e => { e.stopPropagation(); setShow(s => !s); }}
      >
        {children || term}
      </span>
      {show && triggerRef.current && ReactDOM.createPortal(
        <TooltipPopover term={term} explanation={explanation} anchor={triggerRef.current} />,
        document.body
      )}
    </>
  );
}

function TooltipPopover({ term, explanation, anchor }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: -999, left: -999, ready: false });

  useEffect(() => {
    if (!anchor || !ref.current) return;
    const rect = anchor.getBoundingClientRect();
    const popW = ref.current.offsetWidth || 280;
    const popH = ref.current.offsetHeight || 80;

    let top = rect.top - popH - 10;
    let left = rect.left + rect.width / 2 - popW / 2;

    // 防顶部溢出 → 改为显示在下方
    if (top < 8) top = rect.bottom + 8;
    // 防左右溢出
    if (left < 8) left = 8;
    if (left + popW > window.innerWidth - 8) left = window.innerWidth - popW - 8;

    setPos({ top, left, ready: true });
  }, [anchor]);

  return (
    <div
      ref={ref}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        opacity: pos.ready ? 1 : 0,
        padding: "10px 14px",
        background: "rgba(6,78,59,.97)",
        color: "#fff",
        borderRadius: 8,
        fontSize: 12,
        lineHeight: 1.65,
        width: 280,
        maxWidth: "calc(100vw - 16px)",
        boxShadow: "0 6px 20px rgba(0,0,0,.28)",
        zIndex: 9999,
        pointerEvents: "none",
        transition: "opacity 0.12s",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 5, color: "#D4AF37", fontSize: 13 }}>
        {term}
      </div>
      <div style={{ color: "rgba(255,255,255,.9)" }}>{explanation}</div>
    </div>
  );
}
